import { useEffect, useRef, useState } from 'react';
import { oAuthSdkStates } from 'src/common/constants/auth/oauth';
import {
  renderGoogleSignInButton,
  setupGoogleIdentityServices,
} from '../../../utils/auth/googleIdentityServices';
import vars from '../../../vars';

export const useGoogleSignIn = ({
  actionHandler,
  source,
  partnerParams,
  actions,
  isErrorStatus,
  locale,
  buttonOptions,
}) => {
  const buttonContainerRef = useRef(null);
  const [isGisReady, setIsGisReady] = useState(false);
  const initializationStartedRef = useRef(false);

  // Store the callback props in refs to avoid re-renders
  const actionHandlerRef = useRef(actionHandler);
  const sourceRef = useRef(source);
  const partnerParamsRef = useRef(partnerParams);

  // Update refs when props change
  actionHandlerRef.current = actionHandler;
  sourceRef.current = source;
  partnerParamsRef.current = partnerParams;

  // Setup Google Identity Services when component mounts
  useEffect(() => {
    // Set loading state immediately to prevent flash of unstyled content
    actions.updateGoogleAuthSdkStatus(oAuthSdkStates.loading);

    // Check if Google SDK is already loaded and initialized
    const isAlreadyReady = window.google?.accounts?.id;

    if (isAlreadyReady) {
      // SDK is already ready, set to success quickly
      setIsGisReady(true);
      actions.updateGoogleAuthSdkStatus(oAuthSdkStates.success);
      return;
    }

    // Only start initialization once
    if (initializationStartedRef.current) return;
    initializationStartedRef.current = true;

    setupGoogleIdentityServices((error) => {
      if (error) {
        actions.updateGoogleAuthSdkStatus(oAuthSdkStates.error);
        console.error('Failed to setup Google Identity Services:', error);
        initializationStartedRef.current = false; // Allow retry on error
        setIsGisReady(false); // Reset ready state only on error
      } else {
        setIsGisReady(true);
        actions.updateGoogleAuthSdkStatus(oAuthSdkStates.success);
      }
    }, isErrorStatus);

    // Cleanup on unmount
    return () => {
      if (!window.google?.accounts?.id) {
        // Only reset if SDK isn't loaded (so we can retry if needed)
        initializationStartedRef.current = false;
        // Don't reset isGisReady here - let it stay true if Google SDK is loaded
      }
    };
  }, [actions, isErrorStatus]);

  useEffect(() => {
    if (isGisReady && buttonContainerRef.current) {
      // Only render if container is empty to avoid unnecessary re-renders
      if (buttonContainerRef.current.children.length === 0) {
        if (window.google?.accounts?.id) {
          const clientId = vars.get('G_CID');

          window.google.accounts.id.initialize({
            ux_mode: 'popup',
            client_id: clientId,
            auto_select: false,
            cancel_on_tap_outside: false, // Let the popup handle closing
            use_fedcm_for_button: true,
            callback: (credentialResponse) => {
              if (credentialResponse.credential) {
                try {
                  // Parse the JWT token to get user info
                  const payload = JSON.parse(atob(credentialResponse.credential.split('.')[1]));

                  // Call the action handler with the parsed data
                  actionHandlerRef.current(
                    'google',
                    {
                      userId: payload.sub,
                      token: credentialResponse.credential,
                    },
                    sourceRef.current,
                    partnerParamsRef.current,
                  );
                } catch (parseError) {
                  console.error('Error parsing Google credential:', parseError);
                }
              }
            },
          });

          // Render the Google Sign-In button
          renderGoogleSignInButton(buttonContainerRef.current, {
            locale,
            ...(buttonOptions || {}),
          });
        }
      }
    }
  }, [isGisReady, locale]);

  return buttonContainerRef;
};
